import type { PdfjsModule, PdfSource, PdfSourceOptions } from './pdf-types';

const BLOCK_SIZE = 65536;
const DEFAULT_TIMEOUT_MS = 60000;

/** One answer from the server: the bytes it gave, how large the document is, and whether it was a range. */
interface RangeResponse {
  bytes: Uint8Array;
  total: number;
  partial: boolean;
}

/**
 * Reads a PDF over HTTP, by ranges where the server allows it and in one bounded download where it
 * does not. Requests carry neither cookies nor a referrer, so a signed URL is all the authority used.
 *
 * @param {string} url Signed link to the document.
 * @param {PdfjsModule} pdfjs The PDF.js module in use.
 * @param {PdfSourceOptions} options Limit, timeout, progress, and the way out.
 * @return {Promise<PdfSource>} The whole document when it is small, a range transport otherwise.
 */
export async function createRemotePDFSource(url: string, pdfjs: PdfjsModule, options: PdfSourceOptions): Promise<PdfSource> {
  const limit = options.maxFileBytes;
  let intervals: Array<[number, number]> = [];

  // Ranges arrive out of order and may overlap: progress is the part of the document they cover.
  const notify = (begin: number, end: number, total?: number): void => {
    intervals.push([begin, end]);
    intervals.sort((left, right) => left[0] - right[0]);

    const merged: Array<[number, number]> = [];

    for (const interval of intervals) {
      const last = merged.at(-1);

      if (last && interval[0] <= last[1]) last[1] = Math.max(last[1], interval[1]);
      else merged.push([...interval]);
    }

    intervals = merged;

    options.onProgress?.(
      intervals.reduce((sum, [start, stop]) => sum + stop - start, 0),
      total
    );
  };

  const checkSize = (size: number): void => {
    if (!Number.isSafeInteger(size) || size < 0) throw Error('Invalid PDF size returned by the server.');

    if (size > limit) throw Error('The file exceeds the configured size limit.');
  };

  async function request(begin?: number, end?: number, expectedTotal?: number): Promise<RangeResponse | null> {
    const controller = new AbortController();
    const abort = (): void => controller.abort();

    options.signal.addEventListener('abort', abort, { once: true });

    if (options.signal.aborted) controller.abort();

    let timedOut = false;

    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, options.timeoutMs ?? DEFAULT_TIMEOUT_MS);

    try {
      const response = await fetch(url, {
        headers: begin == null ? {} : { Range: `bytes=${begin}-${(end as number) - 1}` },
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
        signal: controller.signal
      });

      if (!response.ok) {
        throw Error(
          `The server returned HTTP ${response.status}. Check the URL and access permissions. ` + 'The signed URL may have expired.'
        );
      }

      const range = response.headers.get('content-range')?.match(/^bytes (\d+)-(\d+)\/(\d+)$/i);

      // A partial answer the viewer cannot place: without Content-Range there is no telling what it holds.
      if (response.status === 206 && !range) {
        await response.body?.cancel();

        if (expectedTotal != null) {
          throw Error('The server stopped exposing Content-Range. Check the document bucket CORS settings.');
        }

        return null;
      }

      const total = range ? Number(range[3]) : Number(response.headers.get('content-length')) || undefined;

      if (total != null) checkSize(total);

      if (expectedTotal != null && (response.status !== 206 || total !== expectedTotal)) {
        throw Error('The PDF changed or the server stopped supporting range requests. Reopen the document.');
      }

      if (range && (Number(range[1]) !== begin || Number(range[2]) !== Math.min(end as number, total as number) - 1)) {
        throw Error('The server returned an unexpected PDF byte range.');
      }

      const expectedBytes = range ? Number(range[2]) - Number(range[1]) + 1 : total;
      const reader = (response.body as ReadableStream<Uint8Array>).getReader();
      const chunks: Array<Uint8Array> = [];
      let size = 0;

      for (;;) {
        const { done, value } = await reader.read();

        if (done) break;

        size += value.byteLength;
        checkSize(size);

        if (expectedBytes != null && size > expectedBytes) throw Error('The PDF response exceeds its advertised size.');

        chunks.push(value);
        notify((range ? (begin as number) : 0) + size - value.byteLength, (range ? (begin as number) : 0) + size, total);
      }

      if (expectedBytes != null && size !== expectedBytes) throw Error('The PDF response was truncated.');

      const bytes = new Uint8Array(size);
      let offset = 0;

      for (const chunk of chunks) {
        bytes.set(chunk, offset);
        offset += chunk.length;
      }

      return { bytes, total: total ?? size, partial: response.status === 206 };
    } catch (error) {
      controller.abort();

      if (timedOut) throw Error('The PDF request timed out. Please try again.');

      if ((error as Error).name === 'TypeError') {
        throw Error('Unable to download the PDF. Check the URL, permissions, and CORS settings.');
      }

      throw error;
    } finally {
      clearTimeout(timer);
      options.signal.removeEventListener('abort', abort);
    }
  }

  let first = await request(0, BLOCK_SIZE);

  // A server that ignores ranges still answers the whole document, which is read within the same limit.
  if (!first) first = await request();

  if (!first) throw Error('The server returned a partial PDF without Content-Range. Check CORS settings.');

  const whole = first;

  if (!whole.partial || whole.bytes.length === whole.total) return { data: whole.bytes, size: whole.total };

  class RangeTransport extends pdfjs.PDFDataRangeTransport {
    override requestDataRange(begin: number, end: number): void {
      request(begin, end, whole.total)
        .then((result) => {
          if (!options.signal.aborted && result) this.onDataRange(begin, result.bytes);
        })
        .catch((error: Error) => {
          if (!options.signal.aborted) options.onError(error);
        });
    }

    override abort(): void {
      options.abort();
    }
  }

  return { range: new RangeTransport(whole.total, whole.bytes, true), length: whole.total, size: whole.total };
}
